import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";

/**
 * Sort key for an enterprise-user search result: [firstName, lastName, email],
 * all lower-cased for case-insensitive comparison. The summary type only
 * carries a single `name`, so we split on the first space (first = before the
 * space, last = the remainder). When a user has no usable name we fall back to
 * the email in the first-name slot so nameless rows still order sensibly
 * against named rows instead of clumping at the top.
 */
function sortKey(user: AskPunttChatUserSummary): [string, string, string] {
  const name = (user.name ?? "").trim().toLowerCase();
  const email = (user.email ?? "").trim().toLowerCase();

  if (name) {
    const spaceIdx = name.indexOf(" ");
    const first = spaceIdx === -1 ? name : name.slice(0, spaceIdx);
    const last = spaceIdx === -1 ? "" : name.slice(spaceIdx + 1).trim();
    return [first, last, email];
  }

  return [email, "", email];
}

/**
 * Comparator ordering enterprise users by first name, then last name, then
 * email — all case-insensitive. Email is the final tie-break (and the fallback
 * for users with no name).
 */
export function compareEnterpriseUsers(
  a: AskPunttChatUserSummary,
  b: AskPunttChatUserSummary,
): number {
  const ka = sortKey(a);
  const kb = sortKey(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
}

/**
 * Returns a new array of enterprise users sorted by
 * first name -> last name -> email (case-insensitive). Does not mutate input.
 */
export function sortEnterpriseUsers<T extends AskPunttChatUserSummary>(
  users: readonly T[],
): T[] {
  return [...users].sort(compareEnterpriseUsers);
}
